import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UserCheck, Plus, Pencil, Trash2, KeyRound, Unlock, Power, Search } from "lucide-react";
import { toast } from "sonner";
import { apiCall, copyText } from "@/components/admin/people/peopleUtils";

export default function AdminTeachers() {
  const [teachers, setTeachers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [schoolFilter, setSchoolFilter] = useState("all");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const loadSchools = useCallback(async () => {
    try { const d = await apiCall("/admin/schools?limit=100"); setSchools(d.schools || []); } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = [];
      if (schoolFilter !== "all") params.push(`school_id=${schoolFilter}`);
      if (search) params.push(`search=${encodeURIComponent(search)}`);
      const d = await apiCall(`/admin/people/teachers${params.length ? "?" + params.join("&") : ""}`);
      setTeachers(d.teachers || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [schoolFilter, search]);

  useEffect(() => { loadSchools(); }, [loadSchools]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const openCreate = () => { setForm({ name: "", email: "", phone: "", school_id: schoolFilter !== "all" && schoolFilter !== "independent" ? schoolFilter : "", subjects: "" }); setModal({ mode: "create" }); };
  const openEdit = (t) => { setForm({ name: t.name || "", email: t.email || "", phone: t.phone || "", school_id: t.school_id || "", subjects: (t.subjects || []).join(", ") }); setModal({ mode: "edit", data: t }); };

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required"); return; }
    setSaving(true);
    try {
      const body = {
        name: form.name, email: form.email, phone: form.phone,
        school_id: form.school_id || null,
        subjects: form.subjects ? form.subjects.split(",").map((s) => s.trim()).filter(Boolean) : [],
      };
      if (modal.mode === "create") {
        const d = await apiCall("/admin/people/teachers", { method: "POST", body });
        toast.success("Teacher created");
        if (d.temp_password) copyText(`${d.teacher_id} / ${d.temp_password}`, "Login");
      } else {
        await apiCall(`/admin/people/teachers/${modal.data.id}`, { method: "PUT", body });
        toast.success("Teacher updated");
      }
      setModal(null); load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const act = async (t, action) => {
    try {
      if (action === "toggle") {
        await apiCall(`/admin/people/teachers/${t.id}/toggle`, { method: "POST", body: { active: !t.is_active } });
        toast.success(!t.is_active ? "Activated" : "Deactivated");
      } else if (action === "delete") {
        if (!window.confirm(`Delete teacher ${t.email}? This cannot be undone.`)) return;
        await apiCall(`/admin/people/teachers/${t.id}`, { method: "DELETE" });
        toast.success("Teacher deleted");
      } else if (action === "reset") {
        const d = await apiCall(`/admin/people/teachers/${t.id}/reset-password`, { method: "POST", body: {} });
        if (d.temp_password) copyText(d.temp_password, "Temp password");
      } else if (action === "unlock") {
        await apiCall(`/admin/people/teachers/${t.id}/unlock`, { method: "POST" });
        toast.success("Unlocked");
      }
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-6" data-testid="admin-teachers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><UserCheck className="h-6 w-6 text-purple-600" /> Teachers</h1>
          <p className="text-sm text-slate-500">Manage all teachers across schools and independent teachers</p>
        </div>
        <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="teacher-create-btn"><Plus className="h-4 w-4 mr-2" /> Add Teacher</Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-md flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search teachers" className="pl-10" data-testid="teachers-search" />
        </div>
        <select className="border border-slate-200 rounded-md p-2 text-sm" value={schoolFilter} onChange={(e) => setSchoolFilter(e.target.value)} data-testid="teachers-school-filter">
          <option value="all">All schools</option>
          <option value="independent">Independent</option>
          {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr><th className="p-4">Name</th><th className="p-4">Login ID</th><th className="p-4">Email</th><th className="p-4">School</th><th className="p-4">Status</th><th className="p-4 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {loading ? (<tr><td colSpan={6} className="p-8 text-center text-slate-400">Loading…</td></tr>)
              : teachers.length === 0 ? (<tr><td colSpan={6} className="p-8 text-center text-slate-400">No teachers found</td></tr>)
              : teachers.map((t) => (
                <tr key={t.id} className="border-t border-slate-100" data-testid={`teacher-row-${t.id}`}>
                  <td className="p-4 font-medium text-slate-800">{t.name || "—"}</td>
                  <td className="p-4 text-slate-600 font-mono text-xs">{t.teacher_id || "—"}</td>
                  <td className="p-4 text-slate-600">{t.email}</td>
                  <td className="p-4 text-slate-600">{t.school_name || "—"}</td>
                  <td className="p-4"><Badge className={t.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}>{t.is_active ? "active" : "inactive"}</Badge></td>
                  <td className="p-4">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" title="Edit" onClick={() => openEdit(t)} data-testid={`teacher-edit-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" title="Activate/Deactivate" onClick={() => act(t, "toggle")} data-testid={`teacher-toggle-${t.id}`}><Power className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" title="Reset password" onClick={() => act(t, "reset")} data-testid={`teacher-reset-${t.id}`}><KeyRound className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" title="Unlock" onClick={() => act(t, "unlock")} data-testid={`teacher-unlock-${t.id}`}><Unlock className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" title="Delete" className="text-red-600 hover:bg-red-50" onClick={() => act(t, "delete")} data-testid={`teacher-delete-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent data-testid="teacher-modal">
          <DialogHeader><DialogTitle>{modal?.mode === "create" ? "Add Teacher" : "Edit Teacher"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Full name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="teacher-name-input" />
            <Input placeholder="Email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="teacher-email-input" />
            <Input placeholder="Phone (optional)" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <select className="w-full border border-slate-200 rounded-md p-2 text-sm" value={form.school_id || ""} onChange={(e) => setForm({ ...form, school_id: e.target.value })} data-testid="teacher-school-select">
              <option value="">Independent (no school)</option>
              {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <Input placeholder="Subjects (comma separated)" value={form.subjects || ""} onChange={(e) => setForm({ ...form, subjects: e.target.value })} data-testid="teacher-subjects-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="teacher-save-btn">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
